import React from 'react';
import { ContentBlock, ContentBlockProps } from '@jbhi-fi/components/src/ContentBlock';
import { PresentationProductCarousel, ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { ContentBanner } from '@jbhi-fi/components/src/ContentBanner/ContentBanner';
import { PresentationVideoCardCarousel } from '@jbhi-fi/components/src/ContentCard/VideoCardCarousel/VideoCardCarousel';
import { mergeResponsiveValues, ResponsiveValue } from '@jbhi-fi/utils/src/responsiveValue/responsiveValue';
import { CardProvider } from '../hooks/useCards';
import { CardGrid } from '@jbhi-fi/components/src/ContentCard';
import { CardGridVariant } from '@jbhi-fi/components/src/ContentCard/CardGrid/CardGrid';
import { HeroBlock } from '@jbhi-fi/components/src/HeroBlock';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { HeadingDivider, HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { useResponsiveValue } from '@jbhi-fi/components/src/hooks/useResponsiveValue';
import { styles } from './Section.css';
import { spacing } from '@jbhi-fi/lanyard-ui/themes';
import { ContentTheme } from '@jbhi-fi/components/src/Contentful/transformers/ThemeTransformer';
import { ListingBlock } from '@jbhi-fi/components/src/ListingBlock';
import { TabsBlock } from '@jbhi-fi/components/src/TabsBlock/TabsBlock';
import { MultiColumnBlock } from '@jbhi-fi/components/src/MultiColumnBlock/MultiColumnBlock';
import { SideBySide } from '@jbhi-fi/components/src/SideBySide/SideBySide';
import { overrideElementTheme } from './overrideElementTheme';

export enum ElementType {
  cardProvider = 'cardProvider',
  cardGrid = 'cardGrid',
  cardGridWithPrimary = 'cardGridWithPrimary',
  heroBlock = 'heroBlock',
  productCarousel = 'productCarousel',
  countPresentationProductCarousel = 'countPresentationProductCarousel',
  presentationProductCarousel = 'presentationProductCarousel',
  presentationVideoCarousel = 'presentationVideoCarousel',
  cardCarousel = 'cardCarousel',
  contentBanner = 'contentBanner',
  listingBlock = 'listingBlock',
  productBlock = 'productBlock',
  multiColumnBlock = 'multiColumnBlock',
  sideBySide = 'sideBySide',
  promoCardCarousel = 'promoCardCarousel',
}
const ElementsWithTheme = [
  ElementType.contentBanner,
  ElementType.listingBlock,
  ElementType.productBlock,
  ElementType.multiColumnBlock,
];
export type SectionElementProps<T> = {
  type: ElementType;
  props: T;
};

export type SectionProps = {
  title?: string;
  href?: string;
  shouldOpenInNewWindow?: boolean;
  spaceBetweenElements?: Partial<ResponsiveValue<number>> | number;
  titlePadding?: Partial<ResponsiveValue<number>> | number;
  titleColor?: 'white' | 'black';
  titleVariant?: HeadingDividerVariant;
  contentBlockProps?: Omit<ContentBlockProps, 'children'>;
  elements: Array<SectionElementProps<any>>;
  contentPalette?: ContentTheme['palette'];
};

const elementMap: Record<
  ElementType,
  { getComponent: () => React.FunctionComponent<any>; defaultProps: { [key: string]: any } }
> = {
  [ElementType.cardProvider]: { getComponent: () => CardProvider, defaultProps: {} },
  [ElementType.cardGrid]: {
    getComponent: () => CardGrid,
    defaultProps: { variant: CardGridVariant.default, shouldShowMobileCarousel: true },
  },
  [ElementType.cardGridWithPrimary]: {
    getComponent: () => CardGrid,
    defaultProps: { variant: CardGridVariant.withPrimary },
  },
  [ElementType.heroBlock]: { getComponent: () => HeroBlock, defaultProps: {} },
  [ElementType.productCarousel]: { getComponent: () => ProductCarousel, defaultProps: {} },
  [ElementType.countPresentationProductCarousel]: {
    getComponent: () => PresentationProductCarousel,
    defaultProps: {},
  },
  [ElementType.presentationProductCarousel]: {
    getComponent: () => PresentationProductCarousel,
    defaultProps: {},
  },
  [ElementType.presentationVideoCarousel]: { getComponent: () => PresentationVideoCardCarousel, defaultProps: {} },
  [ElementType.cardCarousel]: {
    getComponent: () => Carousel,
    defaultProps: {
      fullHeightItems: true,
      showProgressBar: true,
      showArrows: true,
      spaceBetween: 10,
    },
  },
  [ElementType.contentBanner]: { getComponent: () => ContentBanner, defaultProps: {} },
  [ElementType.listingBlock]: { getComponent: () => ListingBlock, defaultProps: {} },
  [ElementType.productBlock]: {
    getComponent: () => TabsBlock,
    defaultProps: {},
  },
  [ElementType.multiColumnBlock]: {
    getComponent: () => MultiColumnBlock,
    defaultProps: {},
  },
  [ElementType.sideBySide]: {
    getComponent: () => SideBySide,
    defaultProps: {},
  },
  [ElementType.promoCardCarousel]: {
    getComponent: () => ListingBlock,
    defaultProps: {},
  },
};
const DEFAULT_CONTENT_BLOCK_PROPS: Partial<ContentBlockProps> = {
  paddingTop: { mobile: 4, tablet: 7.5 },
  paddingBottom: { mobile: 4, tablet: 7.5 },
};

export const Section = ({
  title,
  href,
  shouldOpenInNewWindow,
  spaceBetweenElements,
  titlePadding,
  titleVariant,
  titleColor,
  contentBlockProps = {},
  elements,
  contentPalette,
}: SectionProps) => {
  const titlePaddingResponsive = mergeResponsiveValues<number>({ desktopLg: 9, desktop: 9, mobile: 5 }, titlePadding);
  const titlePaddingFinal = useResponsiveValue<number>({
    xs: titlePaddingResponsive.mobile,
    md: titlePaddingResponsive.desktop,
    lg: titlePaddingResponsive.desktopLg,
  });

  const spaceBetweenElementsResponsive = mergeResponsiveValues<number>(
    { desktopLg: 10, desktop: 10, mobile: 7.5 },
    spaceBetweenElements
  );
  const spaceBetweenElementsFinal = useResponsiveValue<number>({
    xs: spaceBetweenElementsResponsive.mobile,
    md: spaceBetweenElementsResponsive.desktop,
    lg: spaceBetweenElementsResponsive.desktopLg,
  });

  if (!elements?.length) return null;

  return (
    <ContentBlock
      {...DEFAULT_CONTENT_BLOCK_PROPS}
      {...contentBlockProps}
      maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
      style={{ backgroundColor: contentPalette?.background }}
    >
      {title ? (
        <div style={{ paddingBottom: spacing(titlePaddingFinal) }}>
          <HeadingDivider
            heading={title}
            href={href}
            shouldOpenInNewWindow={shouldOpenInNewWindow}
            textColor={contentPalette?.text ?? titleColor}
            variant={titleVariant}
          />
        </div>
      ) : null}
      <div className={styles.elementsContainer} style={{ gap: spacing(spaceBetweenElementsFinal) }}>
        {elements.map((element, i) => {
          const { getComponent, defaultProps } = elementMap[element.type];
          let updatedProps = element.props;
          if (contentPalette && ElementsWithTheme.includes(element.type)) {
            updatedProps = overrideElementTheme(element.type, element.props, contentPalette);
          }
          const Component = getComponent();
          return <Component key={i} {...defaultProps} {...updatedProps} />;
        })}
      </div>
    </ContentBlock>
  );
};
